export interface OrgAggregates {
  connections: number;
  users: number;
  tasks: number;
  items: number;
}

export interface OrgLimits {
  users: number;
  tasks: number;
  connections: number;
  workspaces: number;
}

/** Recurso limitado por el plan — mismo vocabulario que el backend (src/shared/domain/vos/PlanLimits.ts PlanResource). */
export type PlanResource = 'connections' | 'users' | 'tasks' | 'workspaces';

/** Downgrade programado pendiente de aplicarse al final del periodo de facturación actual. */
export interface ScheduledPlanChange {
  targetPlanId: string;
  targetPlanName: string;
  effectiveAt: string;
}

export class Organization {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly slug: string,
    public readonly status: string,
    public readonly planId: string,
    public readonly planName: string,
    public readonly createdDate: string,
    public readonly planStartDate: string,
    public readonly planEndDate: string,
    public readonly domains: string[],
    public readonly aggregates: OrgAggregates,
    public readonly limits: OrgLimits | null,
    /** La org ya consumió su única renovación de trial (gobierna el bloque de /pricing). */
    public readonly trialExtendedOnce: boolean = false,
    /** La org tiene un customer de Stripe (gobierna el botón "Gestionar suscripción" en Ajustes). */
    public readonly hasStripeCustomer: boolean = false,
    /** Motivo de inactividad de la org (gobierna el copy del banner de solo-lectura). String libre desde backend — el mapeo a copy vive en presentation. */
    public readonly inactivityReason: string | null = null,
    /** La org tiene una suscripción de Stripe activa (pago→pago) — gobierna si /pricing ofrece cambiar de plan en vez de un Checkout nuevo. */
    public readonly hasActiveSubscription: boolean = false,
    /** Downgrade de plan programado pendiente (null = sin cambio programado) — gobierna el banner global de OrgLayout (D11). */
    public readonly scheduledPlanChange: ScheduledPlanChange | null = null,
    /** true si el uso actual de la org cabe en el plan destino programado; null cuando no hay downgrade programado. */
    public readonly scheduledPlanChangeFits: boolean | null = null,
    /** Recursos que exceden el plan destino programado cuando scheduledPlanChangeFits es false; null cuando no hay downgrade programado. */
    public readonly scheduledPlanChangeBlockedBy: PlanResource[] | null = null,
    /** La org tiene la renovación automática activada en Stripe (gobierna el bloque "Renovación" del Drawer de Facturación). */
    public readonly autoRenew: boolean = false,
    /** Método de pago de la org (gobierna qué layout de BillingPage renderiza y si /pricing ofrece Checkout). */
    public readonly paymentMethod: 'card' | 'transfer' = 'card',
    /** Id del contacto en Holded — solo relevante cuando paymentMethod es 'transfer' (lectura de facturas en tiempo real). */
    public readonly holdedContactId: string = '',
    /** Si la org recibe el ciclo automático de avisos de trial (D12/D13). Default true — solo `POST /admin/organizations` lo defaultea a false. */
    public readonly sendTrialEmails: boolean = true,
    /** Divisa de liquidación (EUR/USD) fijada la primera vez que la org contrata un plan de pago
     * (multi-currency-plan-catalog-checkout, D4/D10). `null` mientras nunca contrató — gobierna si
     * `/pricing` muestra el selector de divisa y en qué divisa se resuelve el próximo cargo. */
    public readonly settlementCurrency: string | null = null,
    /** Sello de solicitud de baja en autoservicio (self-service-cancellation-request, D1): ISO
     * string cuando la org pidió la baja, `null` cuando no lo ha hecho. Ortogonal a `autoRenew` —
     * ver `DangerZoneSection` para el uso del campo (D13). */
    public readonly cancellationRequestedAt: string | null = null
  ) {}

  static fromPrimitives(data: {
    id: string;
    name: string;
    slug: string;
    status: string;
    planId: string;
    planName: string;
    createdDate: string;
    planStartDate: string;
    planEndDate: string;
    domains: string[];
    aggregates: OrgAggregates;
    limits: OrgLimits | null;
    trialExtendedOnce?: boolean;
    hasStripeCustomer?: boolean;
    inactivityReason?: string | null;
    hasActiveSubscription?: boolean;
    scheduledPlanChange?: ScheduledPlanChange | null;
    scheduledPlanChangeFits?: boolean | null;
    scheduledPlanChangeBlockedBy?: PlanResource[] | null;
    autoRenew?: boolean;
    paymentMethod?: 'card' | 'transfer';
    holdedContactId?: string;
    sendTrialEmails?: boolean;
    settlementCurrency?: string | null;
    cancellationRequestedAt?: string | null;
  }): Organization {
    return new Organization(
      data.id,
      data.name,
      data.slug,
      data.status,
      data.planId,
      data.planName,
      data.createdDate,
      data.planStartDate,
      data.planEndDate,
      data.domains,
      data.aggregates,
      data.limits,
      data.trialExtendedOnce ?? false,
      data.hasStripeCustomer ?? false,
      data.inactivityReason ?? null,
      data.hasActiveSubscription ?? false,
      data.scheduledPlanChange ?? null,
      data.scheduledPlanChangeFits ?? null,
      data.scheduledPlanChangeBlockedBy ?? null,
      data.autoRenew ?? false,
      data.paymentMethod ?? 'card',
      data.holdedContactId ?? '',
      data.sendTrialEmails ?? true,
      data.settlementCurrency ?? null,
      data.cancellationRequestedAt ?? null
    );
  }

  toPrimitives() {
    return {
      id: this.id,
      name: this.name,
      slug: this.slug,
      status: this.status,
      planId: this.planId,
      planName: this.planName,
      createdDate: this.createdDate,
      planStartDate: this.planStartDate,
      planEndDate: this.planEndDate,
      domains: this.domains,
      aggregates: this.aggregates,
      limits: this.limits,
      trialExtendedOnce: this.trialExtendedOnce,
      hasStripeCustomer: this.hasStripeCustomer,
      inactivityReason: this.inactivityReason,
      hasActiveSubscription: this.hasActiveSubscription,
      scheduledPlanChange: this.scheduledPlanChange,
      scheduledPlanChangeFits: this.scheduledPlanChangeFits,
      scheduledPlanChangeBlockedBy: this.scheduledPlanChangeBlockedBy,
      autoRenew: this.autoRenew,
      paymentMethod: this.paymentMethod,
      holdedContactId: this.holdedContactId,
      sendTrialEmails: this.sendTrialEmails,
      settlementCurrency: this.settlementCurrency,
      cancellationRequestedAt: this.cancellationRequestedAt,
    };
  }

  isActive(): boolean {
    return this.status === 'active';
  }
}
